import { ROM_PATCHES, ROM_PATCH_VERSION } from "../generated/rom-patch.js";
import { ROM_HOOKS as RETAIL_ROM_HOOKS } from "../generated/symbols.js";

export { ROM_PATCH_VERSION };

const ROM_SIZE = 0x100000;
const LEVELS = 99;
const MAX_BCD = 999_999;

export type RomPatchGame = "RED" | "BLUE";

function bcd3(n: number): [number, number, number] {
  const d = (v: number) => ((Math.floor(v / 10) % 10) << 4) | (v % 10);
  return [d(Math.floor(n / 10_000)), d(Math.floor(n / 100) % 100), d(n % 100)];
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const u24 = (b: Uint8Array, p: number) => ((b[p] ?? 0) << 16) | ((b[p + 1] ?? 0) << 8) | (b[p + 2] ?? 0);

/** True for every patch version this build can apply (the versions recorded sessions may carry). */
export function isKnownRomPatchVersion(version: number): boolean {
  return Object.hasOwn(ROM_PATCHES, version);
}

/** §11.7.1 hook addresses of the ROM a session ran: retail without a patch, else the patch's. */
export function romHooksFor(version: number | null): { name: string; bank: number; addr: number }[] {
  const hooks = version === null ? RETAIL_ROM_HOOKS : ROM_PATCHES[version]?.hooks;
  if (!hooks) throw new Error(`unknown ROM patch version ${version}`);
  return hooks.map((h) => ({ ...h }));
}

/**
 * §3.5: a copy of the retail ROM with RedChain's patch `version` applied (default: the current one).
 * `dayCarePrices[n - 1]` is the ₽ to grow from level n to n + 1 (99 entries, each ≤ 999,999). The caller
 * has already checked the retail SHA-1 and knows which game it is.
 */
export function applyRomPatch(rom: Uint8Array, dayCarePrices: readonly number[], game: RomPatchGame, version: number = ROM_PATCH_VERSION): Uint8Array {
  if (rom.length !== ROM_SIZE) throw new Error(`ROM is ${rom.length} bytes, expected ${ROM_SIZE}`);
  if (dayCarePrices.length !== LEVELS) throw new Error(`expected ${LEVELS} Day Care prices`);
  const patch = ROM_PATCHES[version];
  if (!patch) throw new Error(`unknown ROM patch version ${version}`);
  const ops = base64ToBytes(patch.runs[game]);
  const out = Uint8Array.from(rom);
  for (let p = 0; p < ops.length; ) {
    const op = ops[p];
    const dst = u24(ops, p + 1);
    const len = u24(ops, p + 4);
    p += 7;
    if (op === 0) {
      out.set(ops.subarray(p, p + len), dst);
      p += len;
    } else {
      const src = u24(ops, p);
      out.set(rom.subarray(src, src + len), dst);
      p += 3;
    }
  }
  dayCarePrices.forEach((p, i) => {
    if (!Number.isInteger(p) || p < 0 || p > MAX_BCD) throw new RangeError(`Day Care price ${p} out of range`);
    out.set(bcd3(p), patch.dayCarePriceTableOffset + i * 3);
  });
  return out;
}
